import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, CalendarClock, CircleDollarSign, Download, Loader2, Pencil, Percent, Receipt, Send } from "lucide-react";
import { toast } from "sonner";
import { api, getApiErrorMessage, getBlobApiErrorMessage } from "../../api/client";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { regionName } from "../../components/ProvinceSelect";
import { useAuth } from "../../contexts/AuthContext";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { saveBlobAs } from "../../lib/blob-tab";
import { calendarDayString, formatCalendarDate, formatInstant, formatInstantTenantDay } from "../../lib/calendar-date";
import { fmtCurrency, parseMoney } from "../../lib/display-labels";
import { unwrap } from "../../lib/api-envelope";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { DhEmpty, DhPanel, DhSpec } from "../downhole/DownholeBits";
import { CcField, RecipientPreview, parseCcInput } from "../downhole/sendBits";
import { RentalBillingLines } from "../../components/rentals/RentalBillingPreview";

/**
 * One rental invoice, /rentals/invoices/:id (#815 PR E). Opened from Finance →
 * Invoices (the Rentals rows were not clickable: there was no screen) and from
 * the order page's invoice list.
 *
 * Everything shown is GET /surface-rentals/invoices/:id as sent: its stored
 * lines (the billing preview's shape — RentalBillingLines), and its own
 * subtotal, tax rows, total, amount paid and balance due. Nothing is summed.
 *
 * WHAT CAN CHANGE (api#989). A DRAFT: the due date and the notes, and either
 * may be cleared. Once SENT or OVERDUE the content is locked: only the notes
 * (never cleared) — the due date is read-only. PAID or VOID: nothing. A field
 * the API refuses comes back as 409 INVOICE_LOCKED, shown as sent.
 *
 * SEND (api#963): the dialog pre-fills To from GET /invoices/:id/recipient; a
 * To left as the API resolved it is not sent, so the API records where the
 * address came from. No billing address is the API's NO_BILLING_EMAIL sentence,
 * shown plainly. Up to 10 CCs. PDF: GET /invoices/:id/pdf.
 *
 * VOID: draft, sent or overdue, never paid. Void is final (there is no revive);
 * it releases the invoice's days and fees, so the order can be invoiced again.
 */

const READ_ROLES = ["admin", "office_staff", "supervisor", "dispatcher"];
const WRITE_ROLES = ["admin", "office_staff"];
const SENDABLE = ["draft", "sent", "overdue"];
const VOIDABLE = ["draft", "sent", "overdue"];
/** Exhaustive over `rental_invoices.status`; anything else is a slate dash. */
const STATUS: Record<string, { label: string; fill: string }> = {
  draft: { label: "Draft", fill: "bg-fi-signal-slate" },
  sent: { label: "Sent", fill: "bg-fi-signal-blue" },
  overdue: { label: "Overdue", fill: "bg-fi-signal-red" },
  paid: { label: "Paid", fill: "bg-fi-signal-green" },
  void: { label: "Void", fill: "bg-fi-signal-slate" },
};
const skinOf = (s: unknown) => (typeof s === "string" && Object.prototype.hasOwnProperty.call(STATUS, s) ? STATUS[s] : { label: "—", fill: "bg-fi-signal-slate" });
/** `sent_to_source` as the API records it (0485's CHECK: exactly these three). */
const SENT_SOURCE: Record<string, string> = { accounting_email: "accounting email", main_contact_email: "main contact", entered: "entered when sending" };

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));
const money = (v: unknown) => fmtCurrency(parseMoney(v));
const day = (d: unknown) => formatCalendarDate(str(d), { month: "short", day: "numeric", year: "numeric" }) ?? "—";

const BAND_SECONDARY =
  "inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 bg-fi-brand px-4 text-sm font-bold transition hover:bg-white/10 disabled:opacity-50";
const SECONDARY =
  "inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50 disabled:opacity-50";
const PRIMARY = "fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50";
const INPUT =
  "mt-1 h-9 w-full min-w-0 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-2 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";

type Invoice = Record<string, unknown>;

export default function RentalInvoiceDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const { user } = useAuth();
  const role = (user as { role?: string } | null)?.role ?? "";
  const canRead = READ_ROLES.includes(role);
  const canWrite = WRITE_ROLES.includes(role);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const query = useQuery({
    queryKey: ["rental-invoice", id],
    queryFn: async () => {
      const raw = unwrap<Invoice>((await api.rentals.invoices.get(id)).data);
      return raw && raw.id ? raw : null;
    },
    enabled: canRead && !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const inv = failure ? null : query.data ?? null;
  const loading = canRead && query.isLoading && !failure;
  const status = str(inv?.status) ?? "";

  const downloadPdf = async () => {
    if (!inv) return;
    setPdfBusy(true);
    try {
      saveBlobAs(await api.rentals.invoices.pdf(id), `${str(inv.invoice_number) ?? "invoice"}.pdf`);
    } catch (error) {
      toast.error(await getBlobApiErrorMessage(error, "The PDF could not be made."));
    } finally {
      setPdfBusy(false);
    }
  };

  if (!canRead) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <PageHeaderBand eyebrow="Rental invoice" title="Invoice" onBack={() => navigate(-1)} backLabel="Back" />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)]">
          <p className="mx-auto max-w-[1920px] rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-10 text-center text-sm text-fieldiq-text">
            Invoices are visible to admin, office staff, supervisors and dispatchers.
          </p>
        </section>
      </div>
    );
  }

  const skin = skinOf(inv?.status);
  const lines = Array.isArray(inv?.line_items) ? (inv!.line_items as Record<string, unknown>[]) : [];
  const editable = canWrite && (status === "draft" || status === "sent" || status === "overdue");

  return (
    <div data-testid="rental-invoice-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={inv ? `Rental invoice · ${skin.label}` : "Rental invoice"}
        title={inv ? str(inv.invoice_number) ?? "—" : loading ? "Loading…" : "Invoice"}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          inv ? (
            <>
              <button type="button" className={BAND_SECONDARY} onClick={downloadPdf} disabled={pdfBusy}>
                {pdfBusy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Download className="size-4" aria-hidden="true" />}
                Download PDF
              </button>
              {canWrite && VOIDABLE.includes(status) && (
                <button type="button" className={BAND_SECONDARY} onClick={() => setVoiding(true)}>
                  <Ban className="size-4" aria-hidden="true" />
                  Void
                </button>
              )}
              {canWrite && SENDABLE.includes(status) && (
                <button type="button" className={PRIMARY} onClick={() => setSending(true)}>
                  <Send className="size-4" aria-hidden="true" />
                  Send
                </button>
              )}
            </>
          ) : undefined
        }
        stats={
          !inv
            ? undefined
            : [
                { icon: <Receipt className="size-4" aria-hidden="true" />, label: "Subtotal", sub: "Before tax", tone: "neutral", value: money(inv.subtotal) },
                { icon: <Percent className="size-4" aria-hidden="true" />, label: "Tax", sub: str(inv.jurisdiction) ? `For ${str(inv.jurisdiction)}` : "No jurisdiction", tone: "neutral", value: money(inv.tax_amount) },
                { icon: <CircleDollarSign className="size-4" aria-hidden="true" />, label: "Total", sub: `Balance due ${money(inv.balance_due)}`, tone: "accent", value: money(inv.total) },
                { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: "Due", sub: "Due date", tone: "info", value: day(inv.due_date) },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading invoice" />
            </div>
          ) : !inv ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              {failure ? (
                <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this invoice" />
              ) : (
                <DhEmpty>This invoice does not exist, or it was deleted.</DhEmpty>
              )}
            </div>
          ) : (
            <>
              {status === "void" && (
                <section data-testid="invoice-void" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card p-4">
                  <h2 className="text-base font-extrabold tracking-tight text-fieldiq-text">This invoice is void</h2>
                  <p className="mt-1 text-xs text-fieldiq-muted">A void invoice is final; its days and fees can be invoiced again from the order.</p>
                </section>
              )}
              <DhPanel
                eyebrow="Invoice"
                title="Invoice record"
                testId="rental-invoice-record"
                actions={
                  editable ? (
                    <button type="button" className={SECONDARY} onClick={() => setEditing(true)}>
                      <Pencil className="size-4" aria-hidden="true" />
                      {status === "draft" ? "Edit details" : "Edit notes"}
                    </button>
                  ) : undefined
                }
              >
                <dl className="grid grid-cols-1 gap-x-6 gap-y-4 p-4 sm:grid-cols-2 md:grid-cols-4">
                  <DhSpec
                    label="Status"
                    value={<span className={`inline-block rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink ${skin.fill}`}>{skin.label}</span>}
                  />
                  <DhSpec label="Customer" value={str(inv.customer_name) ?? "—"} />
                  <DhSpec
                    label="Order"
                    value={
                      str(inv.order_id) ? (
                        <Link className="underline underline-offset-2" to={`/rentals/orders/${encodeURIComponent(String(inv.order_id))}`}>
                          {str(inv.order_number) ?? "—"}
                        </Link>
                      ) : (
                        "—"
                      )
                    }
                  />
                  <DhSpec label="Job site" value={str(inv.job_site_name) ?? "—"} />
                  <DhSpec label="Invoice date" value={day(inv.invoice_date)} />
                  <DhSpec label="Due date" value={day(inv.due_date)} />
                  <DhSpec label="Period" value={str(inv.period_start) || str(inv.period_end) ? `${day(inv.period_start)} – ${day(inv.period_end)}` : "—"} />
                  <DhSpec label="Jurisdiction" value={str(inv.jurisdiction) ? `${regionName(String(inv.jurisdiction))} (${str(inv.jurisdiction)})` : "—"} />
                </dl>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-4 border-t border-fieldiq-border p-4 sm:grid-cols-2">
                  <div className="min-w-0">
                    <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Sent</dt>
                    <dd data-testid="invoice-sent" className="mt-1 text-sm font-semibold text-white">
                      {str(inv.sent_at)
                        ? `${formatInstant(str(inv.sent_at), timeZone) ?? "—"} to ${str(inv.sent_to) ?? "—"}${
                            str(inv.sent_to_source) && SENT_SOURCE[String(inv.sent_to_source)] ? ` (${SENT_SOURCE[String(inv.sent_to_source)]})` : ""
                          }${Array.isArray(inv.sent_cc) && inv.sent_cc.length ? `, copied to ${(inv.sent_cc as string[]).join(", ")}` : ""}`
                        : "Not sent"}
                    </dd>
                  </div>
                  <DhSpec label="Paid" value={str(inv.paid_at) ? formatInstantTenantDay(str(inv.paid_at), timeZone) ?? "—" : "—"} />
                </dl>
                {str(inv.notes) && (
                  <p data-testid="invoice-notes" className="whitespace-pre-line border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                    <span className="block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Notes</span>
                    {str(inv.notes)}
                  </p>
                )}
              </DhPanel>

              <DhPanel eyebrow="Billing" title="Lines" testId="rental-invoice-lines" footer={lines.length ? `${displayValue(lines.length)} ${lines.length === 1 ? "line" : "lines"}` : undefined}>
                <div className="space-y-2 p-4 text-sm">
                  <RentalBillingLines lines={lines} empty="This invoice has no lines" />
                  <dl className="ml-auto w-full max-w-sm space-y-1 border-t border-fieldiq-border pt-2">
                    <TotalRow label="Subtotal" value={money(inv.subtotal)} testId="invoice-subtotal" />
                    {[1, 2].map((n) =>
                      str(inv[`tax_rate_${n}_label`]) ? (
                        <TotalRow
                          key={n}
                          label={`${str(inv[`tax_rate_${n}_label`])} (${displayValue(parseMoney(inv[`tax_rate_${n}_percent`]))}%)`}
                          value={money(inv[`tax_rate_${n}_amount`])}
                          testId={`invoice-tax-${n}`}
                        />
                      ) : null,
                    )}
                    <TotalRow label="Total" value={money(inv.total)} strong testId="invoice-total" />
                    <TotalRow label="Amount paid" value={money(inv.amount_paid)} testId="invoice-paid" />
                    <TotalRow label="Balance due" value={money(inv.balance_due)} strong testId="invoice-balance" />
                  </dl>
                </div>
              </DhPanel>
            </>
          )}
        </div>
      </section>

      {canWrite && sending && inv && <SendDialog inv={inv} tz={timeZone} onClose={() => setSending(false)} />}
      {canWrite && editing && inv && <EditDialog inv={inv} onClose={() => setEditing(false)} />}
      {canWrite && voiding && inv && <VoidDialog inv={inv} onClose={() => setVoiding(false)} />}
    </div>
  );
}

function TotalRow({ label, value, strong, testId }: { label: string; value: string; strong?: boolean; testId?: string }) {
  return (
    <div className={`flex justify-between gap-3 ${strong ? "font-extrabold" : ""}`}>
      <dt className={strong ? "text-white" : "text-fieldiq-muted"}>{label}</dt>
      <dd data-testid={testId} className="font-mono tabular-nums text-white">{value}</dd>
    </div>
  );
}

function useRefreshInvoice(id: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["rental-invoice", id] });
    qc.invalidateQueries({ queryKey: ["rental-invoice-recipient", id] });
    qc.invalidateQueries({ queryKey: ["rentals-order"] });
    qc.invalidateQueries({ queryKey: ["finance-invoices-other-verticals"] });
    qc.invalidateQueries({ queryKey: ["invoice-tab-counts"] });
  };
}

function Modal({ label, children, onClose }: { label: string; children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={label} className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
        {children}
      </div>
    </div>
  );
}

function SendDialog({ inv, tz, onClose }: { inv: Invoice; tz: string | null; onClose: () => void }) {
  const id = String(inv.id);
  const number = str(inv.invoice_number) ?? "invoice";
  const preview = useQuery({
    queryKey: ["rental-invoice-recipient", id],
    queryFn: () => api.rentals.invoices.recipient(id).then((r) => r.data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const refresh = useRefreshInvoice(id);
  const resolved = (preview.data?.data?.to ?? null) as { email: string; name: string | null } | null;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [ccText, setCcText] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  // Pre-fill once, from who the API would send to.
  useEffect(() => {
    if (!touched && resolved) {
      setEmail(resolved.email);
      setName(resolved.name ?? "");
    }
  }, [resolved, touched]);
  const cc = useMemo(() => parseCcInput(ccText), [ccText]);
  // The preview said there is no address on file (NO_BILLING_EMAIL, shown
  // above in the API's words): nothing to send to until one is typed.
  const noAddress = !!preview.data?.data && !resolved && email.trim() === "";

  const submit = async () => {
    setRefusal(null);
    if (cc.problem) return;
    // A To left as the API resolved it is not sent, so the API records where
    // the address came from (accounting email / main contact). A typed one is.
    const typed = email.trim();
    const sameAsResolved = !!resolved && typed === resolved.email && (name.trim() || null) === (resolved.name ?? null);
    const body: { email?: string; contact_name?: string; cc?: string[] } = {};
    if (typed && !sameAsResolved) {
      body.email = typed;
      if (name.trim()) body.contact_name = name.trim();
    }
    if (cc.cc.length) body.cc = cc.cc;
    setBusy(true);
    try {
      const sent = unwrap<{ sent_to?: string; sent_cc?: string[] | null }>((await api.rentals.invoices.send(id, body)).data);
      toast.success(`Sent to ${sent?.sent_to ?? "—"}${sent?.sent_cc && sent.sent_cc.length ? `, copied to ${sent.sent_cc.join(", ")}` : ""}`);
      refresh();
      onClose();
    } catch (error) {
      // NO_BILLING_EMAIL, INVOICE_NOT_SENDABLE, CC_INVALID, EMAIL_INVALID, EMAIL_NOT_SENT: the API's words.
      setRefusal(getApiErrorMessage(error, "The invoice was not sent."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal label={`Send ${number}`} onClose={onClose}>
      <header className="border-b border-fieldiq-border px-5 py-4">
        <h2 className="text-base font-extrabold tracking-tight text-fieldiq-text">Send {number}</h2>
      </header>
      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        <RecipientPreview query={preview} tz={tz} subject="who this goes to" />
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="min-w-0 text-xs text-fieldiq-muted">
            To
            <input type="email" className={INPUT} value={email} onChange={(e) => { setTouched(true); setEmail(e.target.value); }} />
          </label>
          <label className="min-w-0 text-xs text-fieldiq-muted">
            Contact name
            <input className={INPUT} value={name} onChange={(e) => { setTouched(true); setName(e.target.value); }} />
          </label>
        </div>
        <CcField value={ccText} onChange={setCcText} problem={ccText.trim() ? cc.problem : null} />
        {refusal && <p role="alert" data-testid="send-refusal" className="text-sm font-medium text-fieldiq-text">{refusal}</p>}
      </div>
      <footer className="flex flex-wrap justify-end gap-2 border-t border-fieldiq-border px-5 py-3">
        <button type="button" className={SECONDARY} onClick={onClose}>Cancel</button>
        <button type="button" className={PRIMARY} disabled={busy || noAddress || !!(ccText.trim() && cc.problem)} onClick={submit}>
          {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Send invoice
        </button>
      </footer>
    </Modal>
  );
}

/**
 * Draft: the due date and the notes; a blank one that had a value is cleared
 * (null). Sent / overdue: the notes only — the due date is locked, and a clear
 * is draft-only, so the API's INVOICE_LOCKED for either is shown as sent.
 */
function EditDialog({ inv, onClose }: { inv: Invoice; onClose: () => void }) {
  const id = String(inv.id);
  const draft = inv.status === "draft";
  const refresh = useRefreshInvoice(id);
  const [due, setDue] = useState(calendarDayString(str(inv.due_date)) ?? "");
  const [notes, setNotes] = useState(str(inv.notes) ?? "");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setRefusal(null);
    const body: Record<string, unknown> = {};
    const beforeDue = calendarDayString(str(inv.due_date)) ?? "";
    if (draft && due !== beforeDue) body.due_date = due === "" ? null : due;
    const beforeNotes = str(inv.notes) ?? "";
    if (notes.trim() !== beforeNotes.trim()) body.notes = notes.trim() === "" ? null : notes.trim();
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      await api.rentals.invoices.update(id, body);
      toast.success("Invoice saved");
      refresh();
      onClose();
    } catch (error) {
      setRefusal(getApiErrorMessage(error, "The invoice was not saved."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal label={draft ? "Edit invoice details" : "Edit invoice notes"} onClose={onClose}>
      <header className="border-b border-fieldiq-border px-5 py-4">
        <h2 className="text-base font-extrabold tracking-tight text-fieldiq-text">{draft ? "Edit details" : "Edit notes"}</h2>
        {!draft && <p className="mt-1 text-xs text-fieldiq-muted">This invoice is sent; only its notes can change.</p>}
      </header>
      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {draft && (
          <label className="block text-xs text-fieldiq-muted">
            Due date
            <input type="date" className={INPUT} value={due} onChange={(e) => setDue(e.target.value)} />
          </label>
        )}
        <label className="block text-xs text-fieldiq-muted">
          Notes
          <textarea className={`${INPUT} h-24 py-2`} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        {refusal && <p role="alert" data-testid="edit-refusal" className="text-sm font-medium text-fieldiq-text">{refusal}</p>}
      </div>
      <footer className="flex flex-wrap justify-end gap-2 border-t border-fieldiq-border px-5 py-3">
        <button type="button" className={SECONDARY} onClick={onClose}>Cancel</button>
        <button type="button" className={PRIMARY} disabled={busy} onClick={save}>
          {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Save
        </button>
      </footer>
    </Modal>
  );
}

function VoidDialog({ inv, onClose }: { inv: Invoice; onClose: () => void }) {
  const id = String(inv.id);
  const refresh = useRefreshInvoice(id);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setRefusal(null);
    setBusy(true);
    try {
      await api.rentals.invoices.update(id, { status: "void" });
      toast.success(`${str(inv.invoice_number) ?? "Invoice"} is void`);
      refresh();
      onClose();
    } catch (error) {
      setRefusal(getApiErrorMessage(error, "The invoice was not voided."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal label={`Void ${str(inv.invoice_number) ?? "invoice"}`} onClose={onClose}>
      <header className="border-b border-fieldiq-border px-5 py-4">
        <h2 className="text-base font-extrabold tracking-tight text-fieldiq-text">Void {str(inv.invoice_number) ?? "invoice"}</h2>
      </header>
      <div className="space-y-3 px-5 py-4 text-sm text-fieldiq-text">
        <p>Void is final. Its days and fees can then be invoiced again from the order.</p>
        {refusal && <p role="alert" data-testid="void-refusal" className="font-medium">{refusal}</p>}
      </div>
      <footer className="flex flex-wrap justify-end gap-2 border-t border-fieldiq-border px-5 py-3">
        <button type="button" className={SECONDARY} onClick={onClose}>Cancel</button>
        <button type="button" className={PRIMARY} disabled={busy} onClick={confirm}>
          {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Void invoice
        </button>
      </footer>
    </Modal>
  );
}
